// Command palette (Cmd/Ctrl+K): navigation and theme; a question goes to the Sentinel chat.
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageSquare, Moon, Search, Sun } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { useTheme } from '@/contexts/theme-context'
import { PALETTE_DESTINATIONS } from './palette-destinations'

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface CommandItem {
  id: string
  label: string
  icon: ReactNode
  action: () => void
  keywords?: string[]
  group: string
}

const ITEM_CLASS = 'w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-accent hover:text-accent-foreground transition-colors'

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { setTheme, resolvedTheme } = useTheme()
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    setInputValue('')
    const t = setTimeout(() => inputRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [open])

  const run = (action: () => void) => {
    onOpenChange(false)
    action()
  }

  const commands: CommandItem[] = [
    ...PALETTE_DESTINATIONS.map(({ id, label, href, icon: Icon, keywords, group }) => ({
      id, label, icon: <Icon className="h-4 w-4" />, action: () => navigate(href), keywords, group,
    })),
    {
      id: 'toggle-theme', label: `Toggle ${resolvedTheme === 'light' ? 'Dark' : 'Light'} Mode`,
      icon: resolvedTheme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />,
      action: () => setTheme(resolvedTheme === 'light' ? 'dark' : 'light'), keywords: ['theme', 'appearance'], group: 'Theme',
    },
  ]

  const query = inputValue.trim()
  const filtered = commands.filter((cmd) => {
    if (!query) return true
    const q = query.toLowerCase()
    return cmd.label.toLowerCase().includes(q) || cmd.keywords?.some((k) => k.toLowerCase().includes(q))
  })
  const grouped = filtered.reduce<Record<string, CommandItem[]>>((acc, cmd) => {
    (acc[cmd.group] ??= []).push(cmd)
    return acc
  }, {})
  const ask = () => run(() => navigate(`/coo/chat?q=${encodeURIComponent(query)}`))

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') onOpenChange(false)
    if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered.length > 0 && query) run(filtered[0].action)
      else if (query) ask()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 gap-0 max-w-xl" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <div className="border-b p-3 pr-12">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              ref={inputRef}
              data-testid="command-palette-input"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search, or ask Sentinel a question…"
              className="w-full h-10 pl-10 pr-3 rounded-lg border border-input bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
        <div className="max-h-[24rem] overflow-y-auto p-3 space-y-4">
          {query && (
            <button type="button" data-testid="command-palette-ask" onClick={ask} className={ITEM_CLASS}>
              <MessageSquare className="h-4 w-4" />
              <span className="truncate">Ask Sentinel: “{query}”</span>
            </button>
          )}
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              <h4 className="text-xs font-medium text-muted-foreground mb-2 px-1">{group}</h4>
              <div className="space-y-1">
                {items.map((cmd) => (
                  <button key={cmd.id} type="button" onClick={() => run(cmd.action)} className={ITEM_CLASS}>
                    {cmd.icon}
                    <span>{cmd.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Hook for command palette keyboard shortcut
export function useCommandPalette() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const down = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  return { open, setOpen }
}
